import { KycStates } from '@ha/api/v2/types/Kyc';
import { theme, Tooltip } from '@ha/dsl';
import {
  CheckCircle1AlternateBold,
  RoadSignWarningAlternateBold,
  TimeClockCircle1AlternateBold,
} from '@ha/icons';
import { TooltipProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'ha/i18n';
import React from 'react';

const useStyles = makeStyles()({
  tooltip: {
    marginBlock: theme.spacing(0.75),
  },
  payoutStateVerified: {
    color: `${theme.palette.secondary.main} !important`,
  },
  payoutStateNotVerified: {
    color: `${theme.palette.negative.main} !important`,
  },
  payoutStateExpiring: {
    color: `${theme.palette.warning.main} !important`,
  },
  payoutStatePending: {
    color: `${theme.palette.warning.main} !important`,
  },
});

function KycStatusTooltip({
  kycStatus,
  children,
}: {
  kycStatus: KycStates;
  children: TooltipProps['children'];
}) {
  const { classes } = useStyles();

  const { T } = useIntl();

  const title = React.useMemo(() => {
    const titles: Partial<Record<KycStates, string>> = {
      [KycStates.VERIFIED]: T('Verified'),
      [KycStates.PENDING]: T('Pending'),
      [KycStates.EXPIRING]: T('Expiring'),
    };

    return titles[kycStatus] ?? T('Not verified');
  }, [T, kycStatus]);

  return (
    <Tooltip
      title={title}
      placement="top-start"
      classes={{ tooltip: classes.tooltip }}
    >
      {children}
    </Tooltip>
  );
}

export function KycStatusBadge({ kycStatus }: { kycStatus: KycStates }) {
  const { classes } = useStyles();

  if (kycStatus === KycStates.VERIFIED)
    return (
      <KycStatusTooltip kycStatus={kycStatus}>
        <CheckCircle1AlternateBold
          data-test-locator="KycStatusBadge - KycStatusIcon"
          className={classes.payoutStateVerified}
        />
      </KycStatusTooltip>
    );

  if (kycStatus === KycStates.PENDING)
    return (
      <KycStatusTooltip kycStatus={kycStatus}>
        <TimeClockCircle1AlternateBold
          data-test-locator="KycStatusBadge - KycStatusIcon"
          className={classes.payoutStatePending}
        />
      </KycStatusTooltip>
    );

  if (kycStatus === KycStates.EXPIRING)
    return (
      <KycStatusTooltip kycStatus={kycStatus}>
        <RoadSignWarningAlternateBold
          data-test-locator="KycStatusBadge - KycStatusIcon"
          className={classes.payoutStateExpiring}
        />
      </KycStatusTooltip>
    );

  return (
    <KycStatusTooltip kycStatus={kycStatus}>
      <RoadSignWarningAlternateBold
        data-test-locator="KycStatusBadge - KycStatusIcon"
        className={classes.payoutStateNotVerified}
      />
    </KycStatusTooltip>
  );
}
